import React from 'react';
import { Dialog, DialogTitle, Typography, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import ShopsHelper from 'helpers/ShopsHelper';

function ProductOptionsDialog({ open, onClose, item, currentOption, stock, shop, onSelectOption }) {
  const { t: tCommon } = useTranslation('common');

  if (!item) return null;

  const productOptions = item.config?.product_options || [];
  const currency = shop?.currency || 'INR';
  const availableStock = stock ?? item.stock ?? 0;
  const unit = item.unit || 'Pcs';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          mb: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827' }}>
          {tCommon('Options') || 'Options'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#9ca3af', '&:hover': { color: '#111827' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: productOptions.length > 1 ? 'repeat(2, 1fr)' : '1fr',
          },
          gap: '16px',
          maxHeight: '65vh',
          overflowY: 'auto',
          py: 0.5,
          px: 0.5,
        }}
      >
        {productOptions.map((opt, index) => {
          const isSelected = currentOption?.label === opt.label;
          const itemName = item.name || '';
          const cardTitle = opt.label?.toLowerCase().includes(itemName.toLowerCase())
            ? opt.label
            : `${itemName} - ${opt.label}`;

          const formattedPrice = ShopsHelper.getAmountFormatted
            ? ShopsHelper.getAmountFormatted(shop, opt.price)
            : Number(opt.price || 0).toFixed(2);

          return (
            <Box
              key={opt.label || index}
              onClick={() => onSelectOption && onSelectOption(opt)}
              sx={{
                border: isSelected ? '2px solid #111827' : '1px solid #e5e7eb',
                borderRadius: '16px',
                p: '16px 20px',
                backgroundColor: isSelected ? '#f8fafc' : '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: '110px',
                transition: 'all 0.15s ease-in-out',
                boxShadow: isSelected ? '0 4px 12px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.03)',
                '&:hover': {
                  borderColor: isSelected ? '#111827' : '#94a3b8',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: '#1f2937',
                    textTransform: 'uppercase',
                    letterSpacing: '0.01em',
                    lineHeight: 1.3,
                  }}
                >
                  {cardTitle}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af', mt: 0.5, fontWeight: 500 }}>
                  {tCommon('Stock') || 'Stock'} {availableStock} {unit}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#dc2626' }}>
                  {currency} {formattedPrice}
                </Typography>

                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: isSelected ? '#16a34a' : '#000000',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: isSelected ? '#15803d' : '#27272a',
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  {isSelected ? (
                    <CheckIcon sx={{ fontSize: 18, color: '#ffffff' }} />
                  ) : (
                    <AddIcon sx={{ fontSize: 18, color: '#ffffff' }} />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Dialog>
  );
}

export default ProductOptionsDialog;
